import React from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import {
  expandGroupedSizeFilterValues,
  mapSelectedSizeFilterValues,
} from '../../../util/searchPageHelpers';
import { parseSelectFilterOptions } from '../../../util/search';
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../../util/types';

import { FieldCheckbox } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './SelectMultipleFilter.module.css';
import { ListingCategory, ListingFieldConfigKey } from '../../../util/enums';
import RenderBrand from './RenderBrand';
import RenderColors from './RenderColors';
import RenderSizes from './RenderSizes';
import { useLocation } from 'react-router-dom';

const alphabeticallySortedFilterNames = new Set([
  ListingFieldConfigKey.CONDITION,
  'type',
  ListingFieldConfigKey.DANCEWEAR_TYPE,
  ListingFieldConfigKey.SHOES_TYPE,
  ListingFieldConfigKey.COSTUMES_TYPE,
  ListingFieldConfigKey.SHOES_SHANK,
]);

const getOptionValue = optionConfig =>
  typeof optionConfig?.option !== 'undefined' ? optionConfig.option : optionConfig?.key;

const getOptionLabel = optionConfig => {
  const value = getOptionValue(optionConfig);
  const fallback = value != null ? `${value}` : '';
  return optionConfig?.label ?? fallback;
};

export const sortCheckboxOptionsByLabel = (options = [], name) => {
  if (!alphabeticallySortedFilterNames.has(name) || !Array.isArray(options)) {
    return Array.isArray(options) ? options : [];
  }

  return [...options].sort((optionA, optionB) =>
    getOptionLabel(optionA).localeCompare(getOptionLabel(optionB), undefined, {
      sensitivity: 'base',
    })
  );
};

const Loaders = ({ count = 5 }) => {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <ContentLoader key={index} speed={2} viewBox="0 0 380 70">
          <rect x="0" y="0" rx="5" ry="5" width="40" height="40" />
          <rect x="80" y="10" rx="4" ry="4" width="300" height="20" />
        </ContentLoader>
      ))}
    </>
  );
};
// SelectMultipleFilter doesn't need array mutators since it doesn't require validation.
// TODO: Live edit didn't work with FieldCheckboxGroup
//       There's a mutation problem: formstate.dirty is not reliable with it.
const GroupOfFieldCheckboxes = props => {
  const { id, className, name, options, legend } = props;
  // Conditionally apply a different class or render specific UI for the color field
  const isColorField = name === ListingFieldConfigKey.COLOR;
  const containerClass = isColorField ? `${className} ${css.colorField}` : className;
  const checkboxOptions = sortCheckboxOptionsByLabel(options, name);
  const location = useLocation();
  const isCostumesCategory =
    new URLSearchParams(location.search).get('pub_category') === ListingCategory.COSTUMES;

  switch (name) {
    case ListingFieldConfigKey.BRAND:
      return (
        <RenderBrand
          options={options}
          id={id}
          name={name}
          className={className}
          containerClass={containerClass}
        />
      );
    case ListingFieldConfigKey.COLOR:
      return (
        <RenderColors
          options={options}
          id={id}
          name={name}
          className={className}
          containerClass={containerClass}
        />
      );
    case ListingFieldConfigKey.SIZE:
      // Only render RenderSizes if the category is NOT "costumes"
      if (!isCostumesCategory) {
        return (
          <RenderSizes
            options={options}
            id={id}
            name={name}
            className={className}
            containerClass={containerClass}
            renderLoader={<Loaders />}
          />
        );
      }

    default:
      break;
  }
  return (
    <fieldset className={className}>
      {legend ? <legend className={css.accessibilityLegend}>{legend}</legend> : null}
      <ul className={css.list}>
        {checkboxOptions.map(optionConfig => {
          const optionRaw = getOptionValue(optionConfig);
          const option = optionRaw != null ? `${optionRaw}` : '';
          const label = getOptionLabel(optionConfig);
          if (!option) {
            return null;
          }
          const fieldId = `${id}.${option}`;
          return (
            <li key={fieldId} className={css.item}>
              <FieldCheckbox id={fieldId} name={name} label={label} value={option} />
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
};

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

// Format URI component's query param: { pub_key: 'has_all:a,b,c' }
const format = (selectedOptions, queryParamName, schemaType, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  const mode = schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  const value = hasOptionsSelected ? `${mode}${selectedOptions.join(',')}` : null;
  return { [queryParamName]: value };
};

/**
 * SelectMultipleFilter component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} props.id - The id
 * @param {string} props.name - The name
 * @param {node} props.label - The label
 * @param {Function} props.getAriaLabel - The function to retrieve the aria label for the component
 * @param {Array<string>} props.queryParamNames - The query param names
 * @param {Object} props.initialValues - The initial values
 * @param {Function} props.onSubmit - The function to handle the submit
 * @param {Array<Object>} props.options - The options
 * @param {SCHEMA_TYPE_ENUM | SCHEMA_TYPE_MULTI_ENUM} props.schemaType - The schema type
 * @param {'has_all' | 'has_any'} props.searchMode - The search mode
 * @param {boolean} [props.showAsPopup] - Whether to show as popup
 * @param {number} [props.contentPlacementOffset] - The content placement offset
 * @returns {JSX.Element}
 */
const SelectMultipleFilter = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    getAriaLabel,
    options,
    initialValues,
    contentPlacementOffset = 0,
    onSubmit,
    queryParamNames,
    schemaType,
    searchMode,
    showAsPopup,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const location = useLocation();

  const queryParamName = getQueryParamName(queryParamNames);
  const hasInitialValues = !!initialValues && !!initialValues[queryParamName];
  // Parse options from param strings like "has_all:a,b,c" or "a,b,c"
  const rawSelectedOptions = hasInitialValues
    ? parseSelectFilterOptions(initialValues[queryParamName])
    : [];
  const isCostumesCategory =
    new URLSearchParams(location.search).get('pub_category') === ListingCategory.COSTUMES;
  const shouldUseGroupedSizeValues = name === ListingFieldConfigKey.SIZE && !isCostumesCategory;
  const selectedOptions = shouldUseGroupedSizeValues
    ? mapSelectedSizeFilterValues(rawSelectedOptions, options)
    : rawSelectedOptions;

  const labelForPopup = hasInitialValues
    ? intl.formatMessage(
        { id: 'SelectMultipleFilter.labelSelected' },
        { labelText: label, count: selectedOptions.length }
      )
    : label;

  const labelSelectionForPlain = hasInitialValues
    ? intl.formatMessage(
        { id: 'SelectMultipleFilterPlainForm.labelSelected' },
        { count: selectedOptions.length }
      )
    : '';

  // pass the initial values with the name key so that
  // they can be passed to the correct field
  const namedInitialValues = { [name]: selectedOptions };

  const handleSubmit = values => {
    const usedValue = values ? values[name] : values;
    const normalizedValue = shouldUseGroupedSizeValues
      ? expandGroupedSizeFilterValues(usedValue, options)
      : usedValue;
    onSubmit(format(normalizedValue, queryParamName, schemaType, searchMode));
  };

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      label={labelForPopup}
      ariaLabel={getAriaLabel(label, selectedOptions.join(', '))}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      <GroupOfFieldCheckboxes
        className={css.fieldGroup}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
        legend={label}
      />
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={label}
      labelSelection={labelSelectionForPlain}
      ariaLabel={getAriaLabel(label, selectedOptions.join(', '))}
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      {...rest}
    >
      <GroupOfFieldCheckboxes
        className={css.fieldGroupPlain}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
        legend={label}
      />
    </FilterPlain>
  );
};

export default SelectMultipleFilter;
